import React, { useEffect, useRef, useState } from 'react';
import { X, CheckCircle2, Loader2, ImageIcon, LogIn } from 'lucide-react';
import { WhiteInkOptionsPicker } from '../../components/WhiteInkOptionsPicker';
import { isWhiteInkEligible } from '../../lib/whiteInkEligibility';

const MB = 1024 * 1024;
const MAX_FILE_MB = 200;
const formatSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < MB) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / MB).toFixed(2)} MB`;
};
const ACCEPT_STRING =
  '.pdf,.ai,.eps,.svg,.png,.jpg,.jpeg,.tif,.tiff,.psd,.webp,image/*,application/pdf,application/postscript,application/illustrator,image/vnd.adobe.photoshop,application/x-photoshop';

// Inline 20 px SVG previews so customers see what they're picking
// before clicking. Strokes use `currentColor` so the glyph inherits
// the radio's text color (white on the active dark-green state,
// gray on hover, neutral when inactive). Pure SVG — zero deps.
const CutlineIcon = ({ shape, className = 'w-5 h-5' }) => {
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (shape) {
    case 'rounded-rectangle':
      return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
          <rect x="3" y="6" width="18" height="12" rx="3.5" {...stroke} />
        </svg>
      );
    case 'circle':
      return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
          <circle cx="12" cy="12" r="8" {...stroke} />
        </svg>
      );
    case 'custom':
      return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
          {/* Wavy freeform outline — suggests an AI-traced die-cut. */}
          <path
            d="M5 9 Q5 5 9 5 Q12 5 13 7 Q14 9 17 8 Q21 7 21 12 Q21 16 17 17 Q14 17 13 19 Q11 21 8 20 Q4 19 4 15 Q4 12 5 9 Z"
            {...stroke}
          />
        </svg>
      );
    case 'rectangle':
    default:
      return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden>
          <rect x="3" y="6" width="18" height="12" rx="0.5" {...stroke} />
        </svg>
      );
  }
};

const CUTLINE_OPTIONS = [
  { id: 'rectangle', label: 'Rectangle', hint: 'Straight sharp corners' },
  { id: 'rounded-rectangle', label: 'Rounded', hint: 'Soft corner radius' },
  { id: 'circle', label: 'Circle', hint: 'Perfect circle/oval' },
  { id: 'custom', label: 'Custom', hint: 'AI traces your artwork outline' },
];

// Simple filename → preview-thumbnail rule. PDFs and AI/EPS/PSD files won't
// render as <img>; we show a generic document badge for them.
const isImageFile = (file) => {
  if (!file) return false;
  const t = (file.type || '').toLowerCase();
  if (t.startsWith('image/')) return true;
  const ext = (file.name || '').toLowerCase().split('.').pop();
  return ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext);
};

/**
 * Mandatory artwork upload + cutline + notes collector pinned to the TOP
 * of Quote Calculator Page 2. The file is POSTed to the backend as soon
 * as the customer drops it so the Live AI Preview can immediately render
 * the *real* artwork via Gemini image-to-image. The returned
 * `staging_artwork_id` is adopted into the order on Place Order.
 */
export const Page2ArtworkSection = ({
  artworkFile,
  setArtworkFile,
  artworkId,           // staging artwork id returned by the backend
  setArtworkId,
  cutlineShape,
  setCutlineShape,
  artworkInstructions,
  setArtworkInstructions,
  // iter114q — White Ink Options moved INTO this card (right under
  // the Cut Shape selector, above the Artwork Notes). Receives the
  // raw selectedFormat + selectedMaterial so it can self-determine
  // eligibility and only render for transparent / clear / specialty
  // substrates (Clear BOPP, Holographic, Foil, DTF).
  whiteInkOption,
  setWhiteInkOption,
  selectedFormat,
  selectedMaterial,
  // Upload pipeline — iter114p lifted these into a shared hook
  // (`useArtworkUpload`) owned by WizardPage2Panel so the merged-into-
  // mockup dropzone (`MockupIdleDropzone`) can fire the SAME upload as
  // the inline "selected-file" UI here. When parent passes them, this
  // component drives uploads via these props instead of its own state.
  handleFile: handleFileProp,
  uploading: uploadingProp,
  error: errorProp,
  clear: clearProp,
  // Auth gating — unauthenticated visitors can *select* a file but we defer
  // the staging upload until they drop their email via the guest prompt.
  isAuthenticated = true,
  onRequireAuth,
  // When the backend rejects an upload with 401, the frontend's `user`
  // state is stale (cookie expired server-side but cached locally).
  // Calling refreshAuth() re-runs /api/auth/me so AuthContext re-syncs,
  // which flips `isAuthenticated` to false and reveals the auth banner
  // instead of leaving the user staring at the cryptic backend error.
  // eslint-disable-next-line no-unused-vars
  refreshAuth,
  // When rendered inside LiveProductPreview's heroSlot (the default Page 2
  // layout), the parent card already shows the VIP badge + hero heading,
  // so we hide the duplicate inner badge to avoid visual competition.
  nested = false,
}) => {
  const fileInputRef = useRef(null);
  // Resolve which upload pipeline this render uses. Parent-supplied
  // handlers WIN when present so the mockup dropzone + the "remove
  // file" button stay coherent across the merged Page 2 panel.
  const handleFile = handleFileProp;
  const uploading = uploadingProp ?? false;
  const error = errorProp ?? null;
  const clear = clearProp;
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  // When the file is a raster image, show a local preview immediately
  // (no network round-trip). PDFs/AI/EPS get a generic document tile.
  useEffect(() => {
    if (!artworkFile || !isImageFile(artworkFile)) {
      setImagePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(artworkFile);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [artworkFile]);


  const handleRemove = () => {
    if (clear) {
      clear();
    } else {
      setArtworkFile(null);
      setArtworkId(null);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <section
      className="relative p-4 bg-gradient-to-br from-[#F4FBF6] via-[#E8F1E6] to-[#F4FBF6] border border-[#6FBF73]/30 rounded-2xl shadow-md overflow-hidden"
      data-testid="page2-artwork-section"
    >
      {/* Animated ribbon — iter114s.69: amber→emerald shine flipped
          to brand-green wash so it reads as a subtle accent on the
          pale-green panel instead of fighting the new light palette. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#6FBF73] via-[#A8D8AC] to-[#6FBF73] bg-[length:200%_100%] animate-[eg3-shine_3s_linear_infinite]"
      />
      <style>{`
        @keyframes eg3-shine {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes eg3-float {
          0%, 100% { transform: translateY(0) rotate(0); }
          50%      { transform: translateY(-4px) rotate(4deg); }
        }
        @keyframes eg3-pulse-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(111,191,115,0.35); }
          50%      { box-shadow: 0 0 0 12px rgba(111,191,115,0); }
        }
      `}</style>

      {/* iter114r — section body reordered per customer request:
          1. Cut shape (first thing the customer should pick once they
             see their mockup above).
          2. White Ink options (only when applicable).
          3. Artwork notes (collapsible).
          4. Artwork details — header + disclaimers + auth banner +
             selected-file row + upload error. Sits at the BOTTOM. */}

      {/* 1. Cutline picker — compact row on light surface */}
      <div>
        <p className="text-[10px] font-bold text-[#2F5D3A] uppercase tracking-[0.15em] mb-1.5">
          Cut shape <span className="text-red-500">*</span>
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
          {CUTLINE_OPTIONS.map((opt) => {
            const active = cutlineShape === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setCutlineShape(active ? '' : opt.id)}
                data-testid={`page2-cutline-${opt.id}`}
                className={`rounded-lg border p-2 text-left transition-all flex items-start gap-2 ${
                  active
                    ? 'border-[#6FBF73] bg-[#6FBF73]/15 animate-pulse-ring'
                    : 'border-[#6FBF73]/25 bg-white/60 hover:border-[#6FBF73]/60'
                }`}
              >
                <span
                  className={`shrink-0 mt-0.5 ${active ? 'text-[#2F5D3A]' : 'text-[#4A7C59]/70'}`}
                  data-testid={`page2-cutline-icon-${opt.id}`}
                >
                  <CutlineIcon shape={opt.id} className="w-5 h-5" />
                </span>
                <span className="flex-1 min-w-0">
                  <p className={`text-xs font-bold leading-tight ${active ? 'text-[#2F5D3A]' : 'text-[#14281D]'}`}>
                    {opt.label}
                  </p>
                  <p className="text-[10px] text-[#1F3D2C]/65 mt-0.5 leading-snug">{opt.hint}</p>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. White Ink Options — renders only for transparent / clear /
          specialty substrates (Clear BOPP, Holographic, Foil, DTF).
          Theme="light" so the cards match the surrounding pale-green card. */}
      {isWhiteInkEligible(selectedFormat, selectedMaterial) && (
        <div className="mt-3" data-testid="page2-white-ink-section">
          <WhiteInkOptionsPicker
            value={whiteInkOption}
            onChange={setWhiteInkOption}
            theme="light"
            compact
          />
        </div>
      )}

      {/* 3. Optional artwork instructions — collapsible. */}
      <details className="mt-3 group">
        <summary className="cursor-pointer text-[10px] font-bold text-[#2F5D3A] uppercase tracking-[0.15em] select-none list-none flex items-center gap-1.5 hover:text-[#14281D]">
          <span className="transition-transform group-open:rotate-90">▸</span>
          Artwork notes <span className="text-[#1F3D2C]/50 normal-case tracking-normal">(optional)</span>
        </summary>
        <textarea
          id="page2-artwork-instructions"
          data-testid="page2-artwork-instructions"
          value={artworkInstructions}
          onChange={(e) => setArtworkInstructions(e.target.value)}
          placeholder="Special notes for our production team — e.g., which colour to emphasize, what to trim, how to align."
          rows={2}
          maxLength={600}
          className="mt-2 w-full text-sm border border-[#6FBF73]/30 rounded-md p-2 focus:border-[#6FBF73] focus:ring-1 focus:ring-[#6FBF73]/40 outline-none resize-y bg-white text-[#14281D] placeholder:text-[#1F3D2C]/40"
        />
      </details>

      {/* 4. Artwork details — header + VIP badge + disclaimers + auth
          banner + selected-file row. Sits BELOW the controls now so the
          customer sees the actionable choices first; this footer
          confirms what we did with their upload + sets expectations
          about the rendered preview's accuracy. */}
      <div className="mt-4 pt-4 border-t border-[#6FBF73]/20">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#6FBF73] text-white text-sm font-black shadow-md"
              style={{ animation: 'eg3-pulse-glow 2.2s ease-in-out infinite' }}
            >
              1
            </span>
            <h3 className="text-base sm:text-lg font-bold text-[#14281D] leading-tight">
              Artwork details
            </h3>
          </div>

          {/* VIP Club badge removed (iter114s.33) — operator asked to scrub
              the entire VIP Club concept from the customer flow. */}
        </div>

        <p
          className="text-[11px] sm:text-xs text-[#1F3D2C]/70 italic mb-2 ml-9 leading-relaxed"
          data-testid="eg3-ai-mockup-disclaimer"
        >
          The live mockup is for visualisation only — your final printed
          product follows the proof PDF you&apos;ll approve before production.
          Cut shape and colour rendering may vary slightly on screen.
        </p>
        <p
          className="text-[11px] sm:text-xs text-[#1F3D2C]/65 italic mb-3 ml-9 leading-relaxed"
          data-testid="eg3-mockup-cycle-hint"
        >
          Your live mockup preview currently defaults to a standard rectangle
          shape. To explore all available finishing options, cycle through
          the preset cut styles — Rectangle, Rounded Corners, Circle, and
          Custom Shape — and watch your mockup update instantly in real time.
          <br />
          <br />
          When applicable, White Ink settings will also dynamically adjust
          the preview. Toggle between &ldquo;Keep White in Design&rdquo; and
          &ldquo;Make White Areas Clear&rdquo; to see how each option affects
          the final appearance of your product.
        </p>

        {!isAuthenticated && (
          <div
            className="mb-3 flex items-start gap-3 rounded-xl border border-[#4A7C59]/30 bg-white/70 p-3"
            data-testid="page2-artwork-auth-banner"
          >
            <LogIn className="w-5 h-5 text-[#4A7C59] mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0 text-sm">
              <p className="font-semibold text-[#14281D]">Sign in to upload & see your Live AI Preview</p>
              <p className="text-[#1F3D2C]/75 text-xs mt-0.5">
                Just drop your email — no full account or password needed. Takes 10 seconds.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onRequireAuth?.()}
              data-testid="page2-artwork-auth-cta"
              className="text-xs font-semibold text-white bg-[#4A7C59] hover:bg-[#3D6A4A] rounded-full px-4 py-1.5 transition-colors flex-shrink-0"
            >
              Continue →
            </button>
          </div>
        )}

        {/* iter114p — inline dropzone REMOVED. The empty-state dropzone
            now lives inside the LiveProductPreview's idle slot
            (`<MockupIdleDropzone>`), wired in WizardPage2Panel. This
            card now shows ONLY the "selected file" preview + cut-shape +
            notes once a file has been uploaded. */}
        {artworkFile && (
          <div
            className="w-full rounded-xl border border-[#6FBF73]/50 bg-white/70 p-3 flex items-center gap-3"
            data-testid="page2-artwork-selected"
          >
            {/* Thumbnail */}
            <div className="w-12 h-12 rounded-lg bg-white border border-[#6FBF73]/40 flex items-center justify-center overflow-hidden flex-shrink-0">
              {imagePreviewUrl ? (
                <img
                  src={imagePreviewUrl}
                  alt="artwork thumbnail"
                  className="w-full h-full object-contain"
                  data-testid="page2-artwork-thumbnail"
                />
              ) : (
                <ImageIcon className="w-6 h-6 text-[#4A7C59]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {uploading ? (
                  <Loader2 className="w-4 h-4 text-[#4A7C59] animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-[#6FBF73]" />
                )}
                <p className="text-sm font-semibold text-[#14281D] truncate">
                  {artworkFile.name}
                </p>
              </div>
              <p className="text-[11px] text-[#1F3D2C]/75 mt-0.5" data-testid="page2-artwork-status">
                {uploading
                  ? 'Uploading to your Live Mockup…'
                  : `${formatSize(artworkFile.size)} · Live Mockup active ✨`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="page2-artwork-replace"
              className="text-[11px] font-semibold text-[#2F5D3A] hover:text-[#6FBF73] px-2 py-1 rounded transition-colors"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={handleRemove}
              data-testid="page2-artwork-remove"
              aria-label="Remove artwork"
              className="p-1 text-[#4A7C59] hover:text-red-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_STRING}
              onChange={(e) => handleFile?.(e.target.files?.[0])}
              className="hidden"
              data-testid="page2-artwork-input"
            />
          </div>
        )}

        {error && (
          <p className="mt-2 text-xs text-red-600" data-testid="page2-artwork-error">
            {error}
          </p>
        )}
      </div>
    </section>
  );
};

export default Page2ArtworkSection;
